import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { CircularProgress, Alert, Box, Stack, Typography, Button } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useTranslation } from "react-i18next";
import OpenCashRegisterModal from "./cash_register/OpenCashRegisterModal";
import CloseCashRegisterModal from "./cash_register/CloseCashRegisterModal";
import { useCashRegister } from "../../../context/CashRegisterContext";
import SalesTerminal from "./SalesTerminal";
import RetailSalesTerminal from "./RetailSalesTerminal";
import { getLocationSalesConfig } from "../../../services/SettingsService";
import { hasStoredPermission } from "../../../utils/dashboardPermissions";


const Sales = () => {

  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { locationId } = useParams();
  const [pendingStoreCreditCode, setPendingStoreCreditCode] = useState(
    hasStoredPermission('APPLY_STORE_CREDIT') ? routerLocation.state?.storeCreditCode || null : null
  );
  const { t } = useTranslation();
  const {
    cashStatus,
    checkCashStatus,
    loading,
    closeCashRegisterModal,
    setCloseCashRegisterModal,
  } = useCashRegister();

  const [isValidTerminal, setIsValidTerminal] = useState(null);
  const [openCashRegisterModal, setOpenCashRegisterModal] = useState(false);
  const [salesViewMode, setSalesViewMode] = useState("touch");

  const terminalId = localStorage.getItem("terminalId");

  useEffect(() => {
    if (!routerLocation.state?.storeCreditCode) return;
    navigate(routerLocation.pathname, { replace: true, state: null });
  }, [navigate, routerLocation.pathname, routerLocation.state]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    window.scrollTo(0, 0);
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const fetchCashStatus = useCallback(async () => {
    if (locationId && terminalId) {
      await checkCashStatus(locationId, terminalId);
    }
  }, [locationId, terminalId, checkCashStatus]);

  useEffect(() => {
    const checkTerminalAccess = async () => {
      if (!terminalId) {
        alert(t("terminal_not_configured"));
        navigate("/");
        return;
      }
      setIsValidTerminal(true);
      await fetchCashStatus();
    };

    checkTerminalAccess();
  }, [fetchCashStatus, navigate, t, terminalId]);

  useEffect(() => {
    if (!locationId) return;
    const loadSalesViewMode = async () => {
      try {
        const config = await getLocationSalesConfig(locationId);
        setSalesViewMode(config.mode === "retail" ? "retail" : "touch");
      } catch {
        setSalesViewMode("touch");
      }
    };
    loadSalesViewMode();
  }, [locationId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!isValidTerminal) return <Alert severity="error">{t("terminal_not_configured")}</Alert>;

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
      {!cashStatus[locationId] ? (
        <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Stack spacing={2} alignItems="center" style={{ marginBottom: "150px" }}>
            <LockOutlinedIcon sx={{ fontSize: 56, color: "text.secondary" }} />
            <Typography variant="h6" color="textSecondary">
              {t("cash_register_closed")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t("open_cash_register_prompt")}
            </Typography>
            <Button variant="contained" color="primary" onClick={() => setOpenCashRegisterModal(true)}>
              {t("open_cash_register")}
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
          {salesViewMode === "retail" ? (
            <RetailSalesTerminal locationId={locationId} initialStoreCreditCode={pendingStoreCreditCode} onStoreCreditConsumed={() => setPendingStoreCreditCode(null)} />
          ) : (
            <SalesTerminal locationId={locationId} initialStoreCreditCode={pendingStoreCreditCode} onStoreCreditConsumed={() => setPendingStoreCreditCode(null)} />
          )}
        </Box>
      )}

      {/* 🔥 Modales */}
      <OpenCashRegisterModal
        open={openCashRegisterModal}
        onClose={() => setOpenCashRegisterModal(false)}
        locationId={locationId}
        terminalId={terminalId}
        onSuccess={fetchCashStatus}
      />

      <CloseCashRegisterModal
        open={closeCashRegisterModal}
        onClose={() => setCloseCashRegisterModal(false)}
        locationId={locationId}
        terminalId={terminalId}
        onSuccess={fetchCashStatus}
      />
    </Box>
  );
};

export default Sales;
